import type { NavigateFunction } from 'react-router-dom'

/**
 * Whether the current window looks like an OAuth popup opened by Taka.
 *
 * This is only a HINT, not the source of truth: the cross-origin OAuth
 * round-trip (app origin → API → provider → API → app) severs `window.opener`
 * via Cross-Origin-Opener-Policy, so a real popup can report `false` here. Use
 * `fromOAuth` (stamped on the redirect by the server) as the authoritative
 * signal and fall back to this only when it's absent.
 */
function isInPopup(): boolean {
  try {
    return !!window.opener && window.opener !== window
  } catch {
    return false
  }
}

/**
 * Finish a post-OAuth picker/confirm step (Instagram confirm, Facebook page
 * picker, LinkedIn target picker).
 *
 * When this is the OAuth popup flow, hand the popup off to the canonical static
 * success page (`/oauth-callback-success.html`) — the same page every other
 * provider already lands on. It:
 *   1. Signals the opener via localStorage + BroadcastChannel. Both are
 *      same-origin and DO NOT depend on `window.opener`, so the opener's
 *      `openOAuthPopup` verifies the connection even after the cross-origin
 *      round-trip nulls the opener (the "We couldn't verify the connection."
 *      bug).
 *   2. Retries `window.close()`, and — because a COOP-severed popup usually
 *      can't be closed by script at all — falls back to a clean "✓ Connected,
 *      you can close this window" screen. That's far better than stranding the
 *      user inside the full app on the integrations screen, which is what
 *      `navigate('/integrations')` did when `window.opener` was missing.
 *
 * When this isn't the OAuth flow (the user opened the picker manually from
 * Settings), just navigate to the integrations page.
 */
export function finishOAuthPickerStep(opts: {
  provider: string
  fromOAuth: boolean
  navigate: NavigateFunction
}): void {
  const { provider, fromOAuth, navigate } = opts
  if (fromOAuth || isInPopup()) {
    const params = new URLSearchParams({ provider, appUrl: window.location.origin })
    window.location.replace(`/oauth-callback-success.html?${params.toString()}`)
    return
  }
  navigate('/integrations')
}
